
// IITBombaySpokenTutorial.jsx
import React from "react";
import { Link } from "react-router-dom";
import InnerHero from "../../components/InnerHero";
import {
    ArrowLeft, BookOpen, CheckCircle2, Code2, GraduationCap,
    Laptop, Users, Award, ExternalLink
} from "lucide-react";

const dashboard = [
    ["2016", "Authorized Since"],
    ["11+", "Technologies"],
    ["2150+", "Largest FDP"],
    ["IIT Bombay", "Resource Centre"],
];

const objectives = [
    "Promote self-learning through technology-enabled education.",
    "Enhance students' employability through industry-relevant software skills.",
    "Provide access to high-quality learning resources developed by IIT Bombay.",
    "Encourage adoption of Free and Open Source Software (FOSS).",
    "Facilitate IIT Bombay certification opportunities.",
    "Support faculty and students in continuous skill development."
];

const technologies = [
    "Linux", "Scilab", "LaTeX", "PHP & MySQL", "Java", "C", "C++",
    "LibreOffice", "Python", "Moodle LMS", "Drupal", "Emerging FOSS Technologies"
];

const benefits = [
    ["Self-Learning Mode", "Students learn independently through structured video tutorials."],
    ["No Expert Requirement", "Self-explanatory tutorials eliminate the need for expert instructors."],
    ["Curriculum Mapping", "Courses are aligned with B.E. curriculum."],
    ["IIT Bombay Certification", "Certificates after successful online assessment."],
    ["Flexible Learning", "Laboratory sessions or personal study."],
    ["Skill Enhancement", "Practical software and programming skills."],
    ["Career Advantage", "Improves employability and higher education prospects."],
    ["Entrepreneurial Support", "Encourages cost-effective open-source software usage."]
];

const coordinators = [
    ["Prof. Nikhil Kulkarni", "ECE", "Committee In-charge"],
    ["Dr. Gururaj Hatti", "Mechanical Engineering", "Member"],
    ["Dr. Ashik B", "Civil Engineering", "Member"],
    ["Prof. Kanchan Khokale", "CSE (AI & ML)", "Member"],
    ["Prof. Pranesh Kulkarni", "Computer Science & Engineering", "Member"],
    ["Prof. Prakash C", "Electrical & Electronics Engineering", "Member"],
];

const fdps = [
    {
        title: "National FDP on Moodle LMS",
        duration: "04 May 2020 – 08 May 2020",
        mode: "Online",
        participants: "2150+ Faculty Members",
        coordinator: "Prof. Gururaj Hatti"
    },
    {
        title: "National FDP on Drupal",
        duration: "14 Sept 2020 – 18 Sept 2020",
        mode: "Online",
        participants: "26 Faculty Members",
        coordinator: "Prof. Gururaj Hatti, Prof. Ravindra Patil, Prof. Pranesh Kulkarni"
    }
];

const highlights = [
    "Authorized IIT Bombay Spoken Tutorial Resource Centre since 2016.",
    "Promotes Free and Open Source Software learning.",
    "Self-paced learning model.",
    "IIT Bombay certification opportunities.",
    "Curriculum-integrated training.",
    "Dedicated departmental coordinators.",
    "Regular Faculty Development Programs.",
    "Enhances technical competency, employability and entrepreneurship."
];

const quickFacts = [
    ["Centre", "IIT Bombay Spoken Tutorial"],
    ["Since", "2016"],
    ["Recognition", "NME-ICT, Ministry of Education"],
    ["Learning", "Self-paced"],
    ["Certification", "IIT Bombay"],
    ["Technologies", "11+"]
];

export default function IITBombaySpokenTutorial() {
    return (
        <main>
            <InnerHero
                title="IIT Bombay Spoken Tutorial Resource Centre"
                image="/CampusLife/SpokenTutorial/hero.jpg"
                breadcrumbs={[
                    { label: "Campus Life", to: "/campus" },
                    { label: "IIT Bombay Spoken Tutorial" }
                ]}
            />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <Link to="/campus" className="inline-flex items-center gap-2 text-brand font-semibold mb-8">
                    <ArrowLeft size={16} />Back to Campus Life
                </Link>

                <div className="grid lg:grid-cols-3 gap-10">
                    <div className="lg:col-span-2 space-y-10">

                        <section className="bg-white border border-brand/15 p-8">
                            <h2 className="text-4xl text-brand font-semibold mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                                About the Program
                            </h2>
                            <p className="leading-relaxed text-[#2a2a2a]/85">
                                KLS VDIT has been an authorized IIT Bombay Spoken Tutorial Resource Centre since 2016.
                                The Spoken Tutorial Project is an initiative of the National Mission on Education through
                                Information and Communication Technology (NME-ICT), recognized by the Ministry of
                                Education, Government of India and developed by IIT Bombay.
                            </p>
                            <p className="leading-relaxed text-[#2a2a2a]/85 mt-4">
                                The project promotes Free and Open Source Software (FOSS) through self-paced video tutorials,
                                helping students and faculty develop practical software skills and improve employability,
                                higher education opportunities and entrepreneurship.
                            </p>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
                                {dashboard.map(([v, l]) => (
                                    <div key={l} className="bg-[#7a1d2c] p-4 text-white">
                                        <div className="text-3xl font-semibold">{v}</div>
                                        <div className="uppercase tracking-widest text-xs mt-2">{l}</div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Objectives</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                {objectives.map(o => (
                                    <div key={o} className="flex gap-3 border border-brand/10 p-4">
                                        <CheckCircle2 className="text-brand" size={18} /><span>{o}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Technologies Covered</h3>
                            <div className="grid sm:grid-cols-3 gap-3">
                                {technologies.map(t => <div key={t} className="border border-brand/10 p-3 flex items-center gap-2"><Code2 size={16} className="text-brand" />{t}</div>)}
                            </div>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Benefits of the Program</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                {benefits.map(([t, d]) => (
                                    <div key={t} className="border border-brand/10 p-4">
                                        <h4 className="font-semibold text-brand">{t}</h4>
                                        <p className="mt-2 text-sm">{d}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Resource Centre Coordinator</h3>
                            <p><strong>Prof. Nikhil Kulkarni</strong></p>
                            <p>Resource Centre Coordinator & SPOC</p>
                            <p>Assistant Professor, Department of Electronics & Communication Engineering</p>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Department Coordinators</h3>
                            <table className="w-full border-collapse">
                                <thead><tr><th>Name</th><th>Department</th><th>Role</th></tr></thead>
                                <tbody>
                                    {coordinators.map(r => <tr key={r[0]}><td>{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td></tr>)}
                                </tbody></table>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Faculty Development Programs</h3>
                            <div className="grid md:grid-cols-2 gap-5">
                                {fdps.map(f => (
                                    <div key={f.title} className="border border-brand/10 p-5">
                                        <h4 className="font-semibold text-brand">{f.title}</h4>
                                        <p><strong>Duration:</strong> {f.duration}</p>
                                        <p><strong>Mode:</strong> {f.mode}</p>
                                        <p><strong>Participants:</strong> {f.participants}</p>
                                        <p><strong>Coordinator:</strong> {f.coordinator}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Key Highlights</h3>
                            <div className="grid md:grid-cols-2 gap-3">
                                {highlights.map(h => <div key={h} className="flex gap-2"><Award size={16} className="text-brand" />{h}</div>)}
                            </div>
                        </section>

                        <section className="bg-white border border-brand/15 p-8">
                            <h3 className="text-3xl text-brand font-semibold mb-5">Useful Links</h3>
                            <ul className="space-y-2">
                                <li><a href="#" className="text-brand inline-flex items-center gap-2">IIT Bombay Spoken Tutorial Project <ExternalLink size={14} /></a></li>
                                <li><a href="#" className="text-brand inline-flex items-center gap-2">KLS VDIT Official Website <ExternalLink size={14} /></a></li>
                            </ul>
                        </section>

                    </div>

                    <aside className="bg-white border border-brand/15 p-5 h-fit sticky top-36">
                        <h4 className="uppercase text-xs tracking-widest text-brand font-semibold mb-4">Quick Facts</h4>
                        <div className="space-y-3">
                            {quickFacts.map(([l, v]) => <div key={l}><p className="text-xs">{l}</p><p className="font-semibold">{v}</p></div>)}
                        </div>
                    </aside>

                </div>
            </section>
        </main>
    );
}
